import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import TripleCta from '../content/TripleCta';
import Testimonials from '../content/Testimonials';
import CateringFaq from '../faqs/CateringFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

function BreakfastCatering() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  const featuredTrucks = { food_categories: 'breakfast' };

  return (
    <div className='CateringPage'>
      <Meta
        title={`Breakfast Food ${vehicle_type} Catering`}
        type='website'
        canonical='/food-truck-catering/breakfast-catering'
        image='https://static.seattlefoodtruck.com/photos/catering-header.webp'
        description={`Book breakfast food ${vehicleType} catering in ${city_name} for corporate mornings, brunch events, and team breakfasts. Pancakes, burritos, coffee & more.`}
        structuredData={{
          cateringService: {
            name: `Breakfast Food ${vehicle_type} Catering in ${city_name}`,
            description: `Book breakfast food ${vehicleType} catering in ${city_name} for corporate mornings, brunch events, and team breakfasts.`,
            url: '/food-truck-catering/breakfast-catering',
            serviceType: 'Breakfast Food Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Breakfast Catering', item: '/food-truck-catering/breakfast-catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering-header.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Breakfast Food {vehicle_type} Catering — Start Your Event Right</h1>
            <p>Nothing kickstarts a great event like a delicious breakfast. Whether you&#39;re fueling a <Link to='/food-truck-catering/corporate-catering'>corporate meeting</Link>, hosting a weekend brunch, or surprising your team with a morning treat, {city_name}&#39;s breakfast food {vehicleType}s serve up fresh, made-to-order meals that energize and delight. From fluffy pancakes and breakfast burritos to artisanal coffee and fresh pastries, {site_title} connects you with the best breakfast caterers in the city.</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Start Your Breakfast Catering Request</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <TripleCta
        cuisine='breakfast'
        image='https://static.seattlefoodtruck.com/photos/catering2.webp'
        optionsText='Choose from pancakes, waffles, breakfast burritos, egg sandwiches, fresh pastries, and more.'
      />

      <FeaturedTrucks title={`Featured Breakfast Food ${vehicle_type}s`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Book a Breakfast Food ${vehicle_type} for Catering?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>Perfect for Every Morning Event</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Corporate Breakfast Catering</h3>
            <p>Start your team&#39;s day right with a breakfast food {vehicleType} at the office. Whether it&#39;s a Monday morning pick-me-up, an all-hands meeting, or an employee appreciation breakfast, our {vehicleType}s bring fresh, hot food right to your door. From grab-and-go options to full breakfast spreads, our vendors customize menus to fit your team&#39;s needs and dietary preferences.</p>
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Brunch Events & Weekend Gatherings</h3>
            <p>Make your brunch unforgettable with a food {vehicleType} serving up everything from avocado toast and acai bowls to classic eggs Benedict and mimosa pairings. {city_name}&#39;s breakfast {vehicleType}s bring the brunch experience to any outdoor venue, park, or private event — no reservations needed.</p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent />

      <Testimonials title="See what our customers are saying" />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta title={`Book a Breakfast Food ${vehicle_type} for Your Event!`} />
      </section>
    </div>
  );
}

export default BreakfastCatering;
